/**
 * Entity-name field module — modular-form replacement for the legacy entity-name field.
 *
 * Replaces:
 *  • Schema → packages/frontend/src/schema/workspaceSchema.ts:104-125
 *      entityName: yup.string().when(['canProceed','businessNumberCountry','entityType'], ...)
 *        // nullable when (canProceed && AU) or Individual; else required + non-empty test
 *  • UI → BusinessDetailsForm.tsx:463-485
 *      AU → read-only display populated from the ABN lookup (lines 463-466);
 *      non-AU → editable <InputFieldV2 name="entityName" .../> (lines 472-485)
 *
 * Preserves the dual nature (R5): AU is read-only (ViewModeField), every other
 * country is a manual input.
 */
import { EntityTypeEnum, Workspace } from '@sr-sdks/api-sdk-axios';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sr/ui-library';
import { Control, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { userService } from '../../../../reducers/permissions/userSlice';
import { useTypedSelector } from '../../../../stateStore';
import {
  isEntityNameEditableForApplication,
  shouldVerifyBusinessNumber,
} from '../../../../utils/helpers/common';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

export type EntityNameSettingsVm = {
  auEntityName: string;
  disabled: boolean;
  isAuAbn: boolean;
  isShown: boolean;
};

/**
 * Legacy visibility (BusinessDetailsForm.tsx:399-431): the field lives inside
 * the `currentCountry && entityType` block, is skipped for Individuals, and —
 * for AU, where it is a read-only display fed by the ABN lookup — is rendered
 * only once it has a value. An empty read-only AU field is a dead control, so
 * it stays hidden until the lookup fills it (APP-2237 QA).
 */
const isEntityNameShown = ({
  businessNumberCountry,
  entityName,
  entityType,
}: {
  businessNumberCountry?: string;
  entityName?: string;
  entityType?: string;
}) => {
  if (!businessNumberCountry || !entityType) return false;
  if (entityType === EntityTypeEnum.Individual) return false;
  if (shouldVerifyBusinessNumber(businessNumberCountry))
    return Boolean(entityName);

  return true;
};

/**
 * Workspace settings view-model for `Entity Name`.
 *
 * Contains the AU read-only/placeholder logic and the settings-specific
 * edit eligibility.
 */
const useEntityNamePropsForWSSettings = (
  control: Control<WorkspaceDetailsFormValues>,
  workspace?: Workspace,
): EntityNameSettingsVm => {
  const currentUser = useTypedSelector(userService.selectors.userMe);

  // If we don't have the current user yet, avoid locking the field early.
  const membershipEditable = currentUser
    ? isEntityNameEditableForApplication(workspace, currentUser)
    : true;

  const businessNumberCountry = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
  });

  const entityName = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.entityName,
  });

  const entityType = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.entityType,
  });

  const isAuAbn = shouldVerifyBusinessNumber(businessNumberCountry);

  // Disabled = AU read-only OR membership stage not eligible (non-AU).
  const disabled = isAuAbn || !membershipEditable;

  return {
    auEntityName: (entityName ?? '').toString(),
    disabled,
    isAuAbn,
    isShown: isEntityNameShown({
      businessNumberCountry,
      entityName,
      entityType,
    }),
  };
};

/**
 * Onboarding view-model for `Entity Name`.
 *
 * TODO: implement onboarding-specific edit eligibility rules when onboarding
 * pages are wired up.
 */
const useEntityNamePropsForOnboarding = (
  _control: Control<WorkspaceDetailsFormValues>,
  _workspace?: Workspace,
): EntityNameSettingsVm => {
  // Onboarding currently reuses the same AU UX as legacy settings:
  // - AU entity name is ABN-lookup sourced, so it is read-only.
  // - If the lookup hasn't filled it yet, we show the empty placeholder.
  //
  // Non-AU should remain editable for onboarding.
  //
  // TODO: implement onboarding-specific edit eligibility for non-AU and/or
  // membership-stage-specific cases, if product requires it.
  const businessNumberCountry = useWatch({
    control: _control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
  });

  const entityName = useWatch({
    control: _control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.entityName,
  });

  const isAuAbn = shouldVerifyBusinessNumber(businessNumberCountry);

  const entityType = useWatch({
    control: _control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.entityType,
  });

  return {
    auEntityName: (entityName ?? '').toString(),
    // AU => read-only, non-AU => editable.
    disabled: Boolean(isAuAbn),
    isAuAbn,
    isShown: isEntityNameShown({
      businessNumberCountry,
      entityName,
      entityType,
    }),
  };
};

interface EntityNameFieldProps {
  auEntityName?: string;
  control: Control<WorkspaceDetailsFormValues>;
  disabled?: boolean;
  isAuAbn?: boolean;
  isShown?: boolean;
}

/**
 * Entity name is populated by the ABN lookup and shown read-only for AU (R5);
 * for every other country it is a manually editable input.
 */
const EntityNameFieldComponent = ({
  auEntityName = '',
  control,
  disabled = false,
  isAuAbn,
  isShown,
}: EntityNameFieldProps) => {
  if (!isShown) return null;

  return (
    <FormField
      name={WORKSPACE_DETAILS_FORM_FIELDS.entityName}
      control={control}
      render={({ field }) => (
        <FormItem>
          <FormLabel asterisk={!isAuAbn}>Entity Name</FormLabel>
          <FormControl>
            <Input
              {...field}
              disabled={disabled}
              value={isAuAbn ? auEntityName : (field.value ?? '')}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const schema = yup
  .string()
  .when(
    ['businessNumberCountry', 'entityType'],
    ([businessNumberCountry, entityType], schema) => {
      /**
       * AU is never required, gate open or closed: the field is populated by
       * the ABN lookup and rendered read-only (R5), so "Please enter Entity
       * Name" was an error the user had no way to clear (APP-2237 QA). An AU
       * workspace that has not been looked up yet is blocked on the business
       * number instead, where the Lookup button is — see
       * BusinessNumberField.schema.
       */
      if (
        businessNumberCountry === 'AU' ||
        entityType === EntityTypeEnum.Individual
      ) {
        return schema.nullable();
      }

      return schema
        .required('Please enter Entity Name')
        .test(
          'is-not-empty-or-space',
          'Please enter a valid Entity Name',
          (value) => Boolean(value?.trim().length),
        );
    },
  );

type EntityNameFieldStatics = {
  schema: yup.AnySchema;
  useEntityNamePropsForOnboarding: typeof useEntityNamePropsForOnboarding;
  useEntityNamePropsForSettings: typeof useEntityNamePropsForWSSettings;
};

type EntityNameFieldWithStatics = typeof EntityNameFieldComponent &
  EntityNameFieldStatics;

export const EntityNameField = Object.assign(EntityNameFieldComponent, {
  schema,
  useEntityNamePropsForOnboarding,
  useEntityNamePropsForSettings: useEntityNamePropsForWSSettings,
}) as EntityNameFieldWithStatics;
